import { CountryCodes } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/countryCodes";

/**
 * Detects a country code from a phone number input string.
 *
 * @param input - The input string that should start with "+" followed by digits
 * @returns The country ID if an exact match is found, null otherwise
 */
export const detectCountryCodeFromInput = (input: string): string | null => {
  if (!input.startsWith("+")) {
    return null;
  }

  // Remove the + and get just the digits
  const digits = input.replace(/\D/g, "");

  if (!digits) {
    return null;
  }

  // Find countries that match the input (exact or partial)
  const allMatches = CountryCodes.filter((country) =>
    country.code.toString().startsWith(digits)
  );

  if (allMatches.length > 0) {
    // If we have multiple matches, prefer the one that's most commonly used
    if (allMatches.length > 1) {
      const preferredCountries = ["US", "GB", "DE", "FR", "AU"];
      const preferredMatch = allMatches.find((country) =>
        preferredCountries.includes(country.id)
      );

      const matchedCountry = preferredMatch || allMatches[0];

      // Only return if it's an exact match
      if (matchedCountry.code.toString() === digits) {
        return matchedCountry.id;
      }

      // For partial matches, wait for more input
      return null;
    } else {
      // Only one match, use it if it's an exact match
      const matchedCountry = allMatches[0];
      if (matchedCountry.code.toString() === digits) {
        return matchedCountry.id;
      }

      // For partial matches, wait for more input
      return null;
    }
  }

  return null;
};

/**
 * Splits a leading "+<country code>" off a phone number input string.
 * Country calling codes are prefix-free, so at most one leading code can match.
 *
 * @param input - The input string that should start with "+" followed by digits
 * @returns The country ID and the remaining national digits, or null if no
 * complete country code has been typed yet
 */
export const splitCountryCodeFromInput = (
  input: string
): { countryId: string; nationalNumber: string } | null => {
  if (!input.startsWith("+")) {
    return null;
  }

  const digits = input.replace(/\D/g, "");

  for (let length = 1; length <= Math.min(4, digits.length); length++) {
    const countryId = detectCountryCodeFromInput(`+${digits.slice(0, length)}`);
    if (countryId) {
      return { countryId, nationalNumber: digits.slice(length) };
    }
  }

  return null;
};
